import { useCallback, useEffect, useMemo, useState } from 'react';
import GamePlayer, { cleanGame, gameThumb } from './GamePlayer';
import { fetchShop, reviewGame, type ArcadeGame, type ShopData } from '../lib/shop';

// ─────────────────────────────────────────────────────────────────────────────
// The teacher's side of the shop (on the Ocean page): approve the games
// students make before they reach the arcade.
// ─────────────────────────────────────────────────────────────────────────────

export default function ShopTeacher({ cid }: { cid: string }) {
  const [data, setData] = useState<ShopData | null>(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<ArcadeGame | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [prices, setPrices] = useState<Record<string, number>>({});

  const load = useCallback(async () => {
    try { setData(await fetchShop(cid)); setError(''); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not load the shop.'); }
  }, [cid]);
  useEffect(() => { void load(); }, [load]);

  const act = async (f: () => Promise<unknown>) => {
    setError('');
    try { await f(); await load(); } catch (e) { setError(e instanceof Error ? e.message : 'That didn’t work.'); }
  };

  if (!data) return error ? <div className="notice bad">{error}</div> : null;
  const levels = data.games.filter(g => g.kind === 'level');
  const pending = levels.filter(g => g.status === 'pending');
  const live = levels.filter(g => g.status === 'approved');

  return (
    <>
      <div className="panel" style={{ marginTop: 20 }}>
        <div className="panel-head"><h3>🕹️ Student-made games</h3></div>
        <p className="small muted">Students build 2D levels and race courses in the Shop and pay 🪙 10 to send one in (they must beat it themselves first). You set the price to play (🪙 1–5) when you approve it and can change it later; makers don’t earn from their games. The 🪙 10 isn’t given back, even if you turn one down. Try it before approving.</p>
        {error && <div className="notice bad" role="alert">{error}</div>}
        {preview && cleanGame(preview.content) && (
          <GamePlayer game={cleanGame(preview.content)!} look={null} title={preview.title} onClose={() => setPreview(null)} />
        )}
        {pending.length === 0 && <p className="muted small">No games waiting for you.</p>}
        {pending.map(g => (
          <div key={g.id} className="review-game">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <div>
                <strong dir="auto">{g.title}</strong>
                <div className="small muted">by {g.author ?? 'a student'}</div>
              </div>
              <button type="button" className="small" onClick={() => setPreview(g)}>▶ Try it</button>
            </div>
            <Thumb content={g.content} />
            <div className="row">
              <input placeholder="Note to the student (optional)" maxLength={200} value={notes[g.id] ?? ''} onChange={e => setNotes({ ...notes, [g.id]: e.target.value })} style={{ flex: 1, minWidth: 180 }} />
              <button type="button" onClick={() => void act(() => reviewGame(g.id, 'rejected', notes[g.id]))}>Turn down</button>
              <label className="row small" style={{ gap: 6 }}>Price to play
                <select value={prices[g.id] ?? 2} onChange={e => setPrices({ ...prices, [g.id]: Number(e.target.value) })} style={{ width: 'auto' }}>
                  {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>🪙 {n}</option>)}
                </select>
              </label>
              <button type="button" className="primary" onClick={() => void act(() => reviewGame(g.id, 'approved', notes[g.id], prices[g.id] ?? 2))}>Approve</button>
            </div>
          </div>
        ))}
        {live.length > 0 && (
          <>
            <h4 style={{ marginBottom: 6 }}>In the arcade</h4>
            {live.map(g => (
              <div key={g.id} className="my-game">
                <div className="grow">
                  <strong dir="auto">{g.title}</strong>
                  <div className="small muted">by {g.author ?? 'a student'} · {g.plays} plays · beaten by {g.clears}</div>
                </div>
                <select aria-label="Price to play" value={g.price} style={{ width: 'auto' }}
                  onChange={e => void act(() => reviewGame(g.id, 'approved', g.note ?? '', Number(e.target.value)))}>
                  {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>🪙 {n}</option>)}
                </select>
                <button type="button" className="small" onClick={() => setPreview(g)}>▶</button>
                <button type="button" className="small" onClick={() => { if (confirm(`Take “${g.title}” out of the arcade?`)) void act(() => reviewGame(g.id, 'removed')); }}>Take down</button>
              </div>
            ))}
          </>
        )}
      </div>

    </>
  );
}

function Thumb({ content }: { content: unknown }) {
  const src = useMemo(() => { const l = cleanGame(content); return l ? gameThumb(l, 520) : ''; }, [content]);
  return src ? <img className="level-thumb big" src={src} alt="The level" /> : null;
}
